// Frontend i18n wrapper.
// Base strings live in i18nBase.js; approved UI-copy changes can be layered
// here without disturbing the broader translation catalogue during review.
import { makeT as makeBaseT, normalizeLang } from "./i18nBase";

export { normalizeLang } from "./i18nBase";

const OVERRIDES = {
  en: {
    "rp.repracticeMeta": "One re-practice available · Uses 1 interview session",
    "rp.repracticeConfirmIntro": "You can re-practice this interview once using the exact same questions. This will use 1 interview session.",
    "rp.repracticeErrNoCredits": "You’ve used all 5 interview sessions available today. Your session allowance resets at midnight.",
    "sessions.today": (used, limit = 5) => `Interview sessions today: ${used} of ${limit} used`,
    "sessions.limitReached": (limit = 5) => `You’ve used all ${limit} interview sessions available today. Your session allowance resets at midnight.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Interview sessions today: ${used} of ${limit} used · Resets at midnight`,
    "iv.clarify": "I didn’t understand the question",
    "iv.clarifying": "Rephrasing question…",
    "iv.clarifyUsed": "Question rephrased",
    "iv.clarifyFail": "We couldn't rephrase this question. Please continue with the original question.",
    "iv.simplifiedQuestion": "Simplified question",
    "rp.clarificationH": "Question clarification requests",
    "rp.clarificationBody": (used, total) => `${used} of ${total} interview questions were rephrased in simpler English.`,
    "rp.clarificationNone": "You did not request a simpler version of any interview question.",
    "rp.clarificationNote": "Clarification requests are reported as a comprehension signal and do not automatically reduce your interview score.",
  },
  "pt-BR": {
    "rp.repracticeMeta": "Uma nova prática disponível · Usa 1 sessão de entrevista",
    "rp.repracticeConfirmIntro": "Você pode praticar esta entrevista novamente uma vez usando exatamente as mesmas perguntas. Isto usará 1 sessão de entrevista.",
    "rp.repracticeErrNoCredits": "Você já usou as 5 sessões de entrevista disponíveis hoje. Seu limite de sessões é renovado à meia-noite.",
    "sessions.today": (used, limit = 5) => `Sessões de entrevista hoje: ${used} de ${limit} usadas`,
    "sessions.limitReached": (limit = 5) => `Você já usou as ${limit} sessões de entrevista disponíveis hoje. Seu limite de sessões é renovado à meia-noite.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Sessões de entrevista hoje: ${used} de ${limit} usadas · Renova à meia-noite`,
    "iv.clarify": "Não entendi a pergunta",
    "iv.clarifying": "Reformulando a pergunta…",
    "iv.clarifyUsed": "Pergunta reformulada",
    "iv.clarifyFail": "Não foi possível reformular esta pergunta. Continue com a pergunta original.",
    "iv.simplifiedQuestion": "Pergunta simplificada",
    "rp.clarificationH": "Pedidos de esclarecimento das perguntas",
    "rp.clarificationBody": (used, total) => `${used} de ${total} perguntas da entrevista foram reformuladas em inglês mais simples.`,
    "rp.clarificationNone": "Você não pediu uma versão mais simples de nenhuma pergunta da entrevista.",
    "rp.clarificationNote": "Os pedidos de esclarecimento são apresentados como um sinal de compreensão e não reduzem automaticamente sua pontuação na entrevista.",
  },
  "es-419": {
    "rp.repracticeMeta": "Una nueva práctica disponible · Usa 1 sesión de entrevista",
    "rp.repracticeConfirmIntro": "Puede practicar esta entrevista una vez más utilizando exactamente las mismas preguntas. Esto utilizará 1 sesión de entrevista.",
    "rp.repracticeErrNoCredits": "Ya utilizó las 5 sesiones de entrevista disponibles hoy. Su límite de sesiones se restablece a medianoche.",
    "sessions.today": (used, limit = 5) => `Sesiones de entrevista hoy: ${used} de ${limit} utilizadas`,
    "sessions.limitReached": (limit = 5) => `Ya utilizó las ${limit} sesiones de entrevista disponibles hoy. Su límite de sesiones se restablece a medianoche.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Sesiones de entrevista hoy: ${used} de ${limit} utilizadas · Se restablece a medianoche`,
    "iv.clarify": "No entendí la pregunta",
    "iv.clarifying": "Reformulando la pregunta…",
    "iv.clarifyUsed": "Pregunta reformulada",
    "iv.clarifyFail": "No pudimos reformular esta pregunta. Continúe con la pregunta original.",
    "iv.simplifiedQuestion": "Pregunta simplificada",
    "rp.clarificationH": "Solicitudes de aclaración de preguntas",
    "rp.clarificationBody": (used, total) => `${used} de ${total} preguntas de la entrevista se reformularon en un inglés más sencillo.`,
    "rp.clarificationNone": "No solicitó una versión más sencilla de ninguna pregunta de la entrevista.",
    "rp.clarificationNote": "Las solicitudes de aclaración se presentan como una señal de comprensión y no reducen automáticamente su puntuación de la entrevista.",
  },
  "fr-CA": {
    "rp.repracticeMeta": "Une nouvelle pratique disponible · Utilise 1 session d’entrevue",
    "rp.repracticeConfirmIntro": "Vous pouvez refaire cette entrevue une seule fois avec exactement les mêmes questions. Cela utilisera 1 session d’entrevue.",
    "rp.repracticeErrNoCredits": "Vous avez utilisé les 5 sessions d’entrevue disponibles aujourd’hui. Votre allocation de sessions est réinitialisée à minuit.",
    "sessions.today": (used, limit = 5) => `Sessions d’entrevue aujourd’hui : ${used} sur ${limit} utilisées`,
    "sessions.limitReached": (limit = 5) => `Vous avez utilisé les ${limit} sessions d’entrevue disponibles aujourd’hui. Votre allocation de sessions est réinitialisée à minuit.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Sessions d’entrevue aujourd’hui : ${used} sur ${limit} utilisées · Réinitialisation à minuit`,
    "iv.clarify": "Je n’ai pas compris la question",
    "iv.clarifying": "Reformulation de la question…",
    "iv.clarifyUsed": "Question reformulée",
    "iv.clarifyFail": "Nous n’avons pas pu reformuler cette question. Veuillez continuer avec la question originale.",
    "iv.simplifiedQuestion": "Question simplifiée",
    "rp.clarificationH": "Demandes de clarification des questions",
    "rp.clarificationBody": (used, total) => `${used} des ${total} questions d’entrevue ont été reformulées en anglais plus simple.`,
    "rp.clarificationNone": "Vous n’avez demandé une version simplifiée d’aucune question d’entrevue.",
    "rp.clarificationNote": "Les demandes de clarification sont présentées comme un indicateur de compréhension et ne réduisent pas automatiquement votre résultat d’entrevue.",
  },
};

export function makeT(lang) {
  const code = normalizeLang(lang);
  const baseT = makeBaseT(lang);
  const table = OVERRIDES[code] || OVERRIDES.en;
  return (key, ...args) => {
    const value = table[key];
    if (typeof value === "function") return value(...args);
    if (value !== undefined) return value;
    return baseT(key, ...args);
  };
}
